<template>
  <div class="donut-wrap">
    <svg :width="size" :height="size" :viewBox="`0 0 ${size} ${size}`" role="img" :aria-label="ariaLabel">
      <!-- Trilha cinza de fundo -->
      <circle
        :cx="center" :cy="center" :r="radius"
        fill="none"
        stroke="#e6e6e6"
        :stroke-width="stroke"
      />

      <!-- Vazio: mantém só a trilha cinza -->
      <template v-if="total > 0">
        <!-- Fatia Próprio (começa no topo) -->
        <circle
          v-if="proprio > 0"
          :cx="center" :cy="center" :r="radius"
          fill="none"
          :stroke="proprioColor"
          :stroke-width="stroke"
          :stroke-dasharray="`${proprioLen} ${circumference - proprioLen}`"
          :stroke-dashoffset="proprioOffset"
          stroke-linecap="butt"
          :transform="`rotate(-90 ${center} ${center})`"
        />
        <!-- Fatia Ônibus -->
        <circle
          v-if="onibus > 0"
          :cx="center" :cy="center" :r="radius"
          fill="none"
          :stroke="onibusColor"
          :stroke-width="stroke"
          :stroke-dasharray="`${onibusLen} ${circumference - onibusLen}`"
          :stroke-dashoffset="onibusOffset"
          stroke-linecap="butt"
          :transform="`rotate(-90 ${center} ${center})`"
        />
      </template>

      <!-- Centro -->
      <g v-if="total > 0">
        <text :x="center" :y="center - 6" text-anchor="middle" class="donut-center-pct" :fill="dominantColor">
          {{ dominantPct }}%
        </text>
        <text :x="center" :y="center + 18" text-anchor="middle" class="donut-center-label">
          {{ dominantLabel }}
        </text>
      </g>
      <g v-else>
        <text :x="center" :y="center + 4" text-anchor="middle" class="donut-empty">
          Sem respostas
        </text>
      </g>
    </svg>

    <div class="donut-legend">
      <div class="legend-item">
        <span class="legend-swatch" :style="{ background: proprioColor }"></span>
        <span class="legend-label">Próprio</span>
        <span class="legend-count">{{ proprio }} <small>({{ pctOf(proprio) }}%)</small></span>
      </div>
      <div class="legend-item">
        <span class="legend-swatch" :style="{ background: onibusColor }"></span>
        <span class="legend-label">Ônibus</span>
        <span class="legend-count">{{ onibus }} <small>({{ pctOf(onibus) }}%)</small></span>
      </div>
      <div class="legend-item legend-item-muted" v-if="semResposta > 0">
        <span class="legend-swatch legend-swatch-muted"></span>
        <span class="legend-label">Sem resposta</span>
        <span class="legend-count">{{ semResposta }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { getTransporte } from '../../lib/transportes'

export default {
  name: 'TransporteDonut',
  props: {
    acampantes: { type: Array, required: true },
    size: { type: Number, default: 180 },
    stroke: { type: Number, default: 26 }
  },
  computed: {
    center() { return this.size / 2 },
    radius() { return (this.size - this.stroke) / 2 },
    circumference() { return 2 * Math.PI * this.radius },
    proprioColor() { return getTransporte('PROPRIO')?.hex || '#3366cc' },
    onibusColor() { return getTransporte('ONIBUS')?.hex || '#e67e22' },
    proprio() { return this.acampantes.filter(a => a.transporte === 'PROPRIO').length },
    onibus() { return this.acampantes.filter(a => a.transporte === 'ONIBUS').length },
    semResposta() { return this.acampantes.filter(a => !a.transporte).length },
    total() { return this.proprio + this.onibus },
    proprioLen() { return this.total ? (this.proprio / this.total) * this.circumference : 0 },
    onibusLen() { return this.total ? (this.onibus / this.total) * this.circumference : 0 },
    proprioOffset() { return 0 },
    onibusOffset() {
      // Arco do Ônibus começa onde o Próprio termina (deslocamento negativo na origem rotacionada)
      return -this.proprioLen
    },
    dominantPct() {
      if (this.total === 0) return 0
      return Math.round((Math.max(this.proprio, this.onibus) / this.total) * 100)
    },
    dominantLabel() {
      if (this.total === 0) return ''
      return this.proprio >= this.onibus ? 'Próprio' : 'Ônibus'
    },
    dominantColor() {
      return this.proprio >= this.onibus ? this.proprioColor : this.onibusColor
    },
    ariaLabel() {
      if (this.total === 0) return 'Sem respostas'
      return `${this.pctOf(this.proprio)}% Próprio, ${this.pctOf(this.onibus)}% Ônibus`
    }
  },
  methods: {
    pctOf(value) {
      if (this.total === 0) return 0
      return Math.round((value / this.total) * 100)
    }
  }
}
</script>

<style scoped>
.donut-wrap {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  flex-wrap: wrap;
  justify-content: center;
}
.donut-center-pct {
  font-size: 26px;
  font-weight: 800;
  font-family: var(--font-primary);
}
.donut-center-label {
  font-size: 11px;
  fill: var(--color-text-muted);
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
.donut-empty {
  font-size: 12px;
  fill: var(--color-text-muted);
  font-weight: 600;
}

.donut-legend {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}
.legend-item {
  display: grid;
  grid-template-columns: 14px auto 1fr;
  align-items: center;
  gap: var(--spacing-sm);
  min-width: 160px;
}
.legend-swatch {
  width: 14px; height: 14px; border-radius: 3px;
}
.legend-swatch-muted { background: #d1d5db; }
.legend-item-muted .legend-label,
.legend-item-muted .legend-count { color: var(--color-text-muted); font-weight: 600; }
.legend-label {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-text-primary);
}
.legend-count {
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--color-text-primary);
  text-align: right;
}
.legend-count small {
  color: var(--color-text-muted);
  font-weight: 500;
}
</style>
